import { GridRenderCellParams } from '@mui/x-data-grid'
import { Icon, Tooltip } from 'components'
import strings from 'strings'
import { numberComparator, numberOrStringComparator, stringDateComparator } from 'tools/comparators'
import { formatRiskCell } from 'tools/helper'
import { EldProviderCell } from './EldProviderCell'
import { formatDate } from 'tools/helper'


const t = strings.TABLE_DEFAULTS

const getMilesColumnHeader = (periodFilter: string) => {
  switch (periodFilter) {
    case '30':
      return 'Miles (Last 30 Days)'
    case '60':
      return 'Miles (Last 60 Days)'
    case '90':
      return 'Miles (Last 90 Days)'
    default:
      return 'Miles (Last 30 Days)'
  }
}

const getCarrierHeartbeatVehiclesColumns = (periodFilter: string = '30', isReadonlyDashboard: boolean) => [
  {
    field: 'rowId',
    headerName: 'ID',
    width: 80,
    minWidth: 80,
  },
  {
    field: 'assetEldId',
    headerName: 'Vehicle ID',
    width: 80,
    minWidth: 80,
    flex: 0.5,
    sortable: true,
    sortComparator: numberOrStringComparator,
    description: 'Asset ELD ID',
    renderCell: function (params: GridRenderCellParams) {
      return (
        <Tooltip
          title={params.value}
          containerClassName="ellipsis"
          maxWidth={210}
        >
          {params.value}
        </Tooltip>
      )
    },
  },
  {
    field: 'identificationNo',
    headerName: 'VIN',
    width: 120,
    minWidth: 120,
    flex: 0.9,
    sortable: true,
    sortComparator: numberOrStringComparator,
    renderCell: function (params: GridRenderCellParams) {
      return (
        <Tooltip
          title={params.value}
          containerClassName="ellipsis"
          maxWidth={210}
        >
          {params.value}
        </Tooltip>
      )
    },
  },
  {
    field: 'vehicleClass',
    headerName: 'Class',
    width: 90, 
    minWidth: 90, 
    flex: 0.6,
    renderCell: function (params: GridRenderCellParams) {
      return (
        <Tooltip
          title={params.value}
          containerClassName="ellipsis"
          maxWidth={210}
        >
          {params.value}
        </Tooltip>
      )
    },
  },
  {
    field: 'vehicleMake',
    headerName: 'Make',
    width: 100,
    minWidth: 100,
    flex: 0.7,
    renderCell: function (params: GridRenderCellParams) {
      return (
        <Tooltip
          title={params.value}
          containerClassName="ellipsis"
          maxWidth={210}
        >
          {params.value}
        </Tooltip>
      )
    },
  },
  {
    field: 'vehicleModel',
    headerName: 'Model',
    width: 80,
    minWidth: 80, 
    flex: 0.7,
    renderCell: function (params: GridRenderCellParams) {
      return (
        <Tooltip
          title={params.value}
          containerClassName="ellipsis"
          maxWidth={210}
        >
          {params.value}
        </Tooltip>
      )
    },
  },
  {
    field: 'vehicleYear',
    headerName: 'Year',
    width: 70,
    minWidth: 70,
    flex: 0.4,
  },
  {
    field: 'policyList',
    headerName: 'Policy List',
    width: 80,
    minWidth: 80,
    flex: 0.5,
    sortable: true,
    renderCell: (params: any) =>
      params.value ? (
        <Icon materialIcon="CheckCircle" color="#5FC187" />
      ) : (
        <Icon materialIcon="Cancel" color="#B0B7C3" />
      ),
  },
  {
    field: 'telematics',
    headerName: 'Telematics',
    width: 80,
    minWidth: 80,
    flex: 0.5,
    sortable: true,
    renderCell: (params: any) =>
      params.value ? (
        <Icon materialIcon="CheckCircle" color="#5FC187" />
      ) : (
        <Icon materialIcon="Cancel" color="#B0B7C3" />
      ),
  },
  {
    field: 'hasCamera',
    headerName: 'Camera',
    minWidth: 60,
    flex: 1,
    sortable: true,
    renderCell: (params: any) =>
      params.value ? (
        <Icon materialIcon="CheckCircle" color="#5FC187" />
      ) : (
        <Icon materialIcon="Cancel" color="#B0B7C3" />
      ),
  },
  {
    field: 'lastTracking',
    headerName: 'Last Tracking',
    width: 120,
    minWidth: 120, 
    flex: 1,
    sortable: true,
    sorter: stringDateComparator,
    renderCell: function (params: GridRenderCellParams) {
      const formattedDate = formatDate(params.value)
      return (
        <Tooltip
          title={formattedDate ? formattedDate : '-'}
          containerClassName="ellipsis"
          maxWidth={210}
        >
          <> {formattedDate ? formattedDate : '-'} </>
        </Tooltip>
      )
    },
  },
  {
    field: 'lastMovement',
    headerName: 'Last Movement',
    width: 120, 
    minWidth: 120, 
    flex: 1,
    sortable: true,
    sorter: stringDateComparator,
    renderCell: function (params: GridRenderCellParams) {
      const formattedDate = formatDate(params.value)
      return (
        <Tooltip
          title={formattedDate ? formattedDate : '-'}
          containerClassName="ellipsis"
          maxWidth={210}
        >
          <> {formattedDate ? formattedDate : '-'} </>
        </Tooltip>
      )
    },
  },
  {
    field: 'eldProviderName',
    headerName: 'ELD Provider',
    width: 100,
    minWidth: 100,
    flex: 0.8,
    sortable: true,
    cellClassName: 'eld-provider-cell',
    renderCell: function (params: GridRenderCellParams) {
      return (
        <EldProviderCell
          eldProviderNames={params.value?.split(', ') || []}
        />
      )
    },
    valueGetter: (value: any, row: any) => {
      const eldProviderName = row.eldProviderName || ''
      if (!eldProviderName || eldProviderName.trim() === '') {
        return t.DASH_VALUE
      }
      return eldProviderName
    }
  },
  {
    field: 'miles',
    headerName: getMilesColumnHeader(periodFilter),
    width: isReadonlyDashboard ? 100 : 80,
    minWidth: isReadonlyDashboard ? 100 : 80,
    flex: isReadonlyDashboard ? 1 : 0.5,
    sortable: true,
    sortComparator: numberComparator,
    valueGetter: (value: any, row: any) => {
      return row.miles !== undefined && row.miles !== null
        ? formatRiskCell(row.miles)
        : t.DASH_VALUE
    },
  },
]

const activeVehiclesActionList = [
  {
    name: 'Add',
    icon: 'icon_add',
    iconSize: 35,
    isDisabled: (params: any) => {
      const hasValidVin =
        params.identificationNo &&
        params.identificationNo !== t.DASH_VALUE &&
        params.identificationNo.trim() !== ''
      return params.policyList || params.pullingEnabled !== true || !hasValidVin
    },
  },
  {
    name: 'Pause',
    icon: 'icon_pause',
    iconSize: 35,
    isDisabled: (params: any) => {
      return params.pullingEnabled !== true
    },
  },
  {
    name: 'Remove',
    icon: 'icon_offboard',
    iconSize: 35,
    isDisabled: (params: any) => {
      return params.policyList === false
    },
  },
]

const pausedVehiclesActionList = [
  {
    name: 'Resume',
    icon: 'icon_resume',
    iconSize: 35,
    isDisabled: (params: any) => {
      return params.pullingEnabled !== false
    },
  },
  {
    name: 'Remove',
    icon: 'icon_offboard',
    iconSize: 35,
    isDisabled: (params: any) => {
      return params.policyList === false
    },
  },
]

export {
  getCarrierHeartbeatVehiclesColumns,
  activeVehiclesActionList,
  pausedVehiclesActionList,
}